import React from "react";
import paisavalaLogo from "../../../../../assets/Image/paisavala.png";
import useFormattedDate from "../../../../../context/useFormattingUtils";
import useFormattingUtils from "../../../../../context/useFormattingUtils";
import useInvoicePrint from "../../Dashboard/Invoice/templates/useInvoicePrint";
import PrintableInvoice from "../../Dashboard/Invoice/templates/PrintableInvoice";
import { normalizeInvoiceForPrint } from "../../Dashboard/Invoice/templates/normalizeInvoiceForPrint";
import { getDefaultInvoiceLayout } from "../../Dashboard/Invoice/templates/invoiceLayoutDefaults";
import { useConfig } from "../../../../../context/ConfigContext";

const POSThermalInvoiceModal = ({ open, onClose, data }) => {
  const { formatDate } = useFormattedDate();
  const { formatCurrency } = useFormattingUtils();
  const { printInvoice } = useInvoicePrint();
  const { invoiceLayoutSettings, storeInfo } = useConfig() || {};
  const layout = (invoiceLayoutSettings && invoiceLayoutSettings.pos) || getDefaultInvoiceLayout("pos");

  if (!open) return null;

  const salesInvoice = data?.sales_invoice || data?.invoice || {};
  const products = Array.isArray(data?.sales_invoice_products)
    ? data.sales_invoice_products
    : Array.isArray(data?.products)
      ? data.products
      : [];

  const safeNumber = (value) => {
    const number = parseFloat(value);
    return Number.isNaN(number) ? 0 : number;
  };

  const safeValue = (value, fallback = "-") => {
    if (value === null || value === undefined || value === "") return fallback;
    return value;
  };

  const safeCurrency = (value) => {
    const amount = safeNumber(value);
    return formatCurrency ? formatCurrency(amount) : amount.toFixed(2);
  };

  const payableAmount =
    safeNumber(salesInvoice?.payable_amount) ||
    safeNumber(salesInvoice?.final_after_discount) ||
    safeNumber(salesInvoice?.total_amount);

  const totalQty = products.reduce(
    (total, item) => total + safeNumber(item?.sold_qty_stock || item?.qty),
    0
  );

  const totalBeforeDiscount = products.reduce(
    (total, item) =>
      total + safeNumber(item?.total_amount || item?.amount || item?.before_discount),
    0
  );

  const totalAfterDiscount = products.reduce(
    (total, item) => total + safeNumber(item?.after_discount || item?.total),
    0
  );

  const additionalDiscount = safeNumber(salesInvoice?.additional_discount_value);
  const roundOff = safeNumber(salesInvoice?.round_off);

  const handlePrint = () => {
    // Routed through the shared settings-driven template system instead of
    // relying on this component's own DOM as the print source.
    printInvoice("pos", data);
  };

  const lineStyle = {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    fontSize: "12px",
    lineHeight: 1.45,
  };

  const mutedText = {
    color: "#64748b",
    fontSize: "11px",
    lineHeight: 1.45,
  };

  return (
    <>
      <style>
        {`
          /* NOTE: printing is now handled entirely by useInvoicePrint()/PrintableInvoice
             (a hidden, settings-driven template mounted off-screen). This modal is only
             the on-screen preview, so it must NOT declare its own @media print rules --
             an old body * { visibility: hidden } override here would hide the new
             template's printable content and silently fall back to this hardcoded look. */

          @media (max-width: 640px) {
            .pos-thermal-modal-card {
              width: 96% !important;
              max-height: 92vh !important;
            }

            .pos-thermal-actions {
              width: 100%;
              justify-content: stretch !important;
            }

            .pos-thermal-actions button {
              flex: 1;
            }
          }
        `}
      </style>

      <div
        style={{
          position: "fixed",
          inset: 0,
          background:
            "radial-gradient(circle at top, rgba(15, 23, 42, 0.72), rgba(2, 6, 23, 0.84))",
          zIndex: 1200,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "24px",
        }}
      >
        <div
          className="pos-thermal-modal-card"
          style={{
            width: "760px",
            maxHeight: "90vh",
            overflowY: "auto",
            background: "#f8fafc",
            borderRadius: "24px",
            boxShadow: "0 28px 80px rgba(2, 6, 23, 0.42)",
            border: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <div
            className="pos-thermal-no-print"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 5,
              background: "#ffffff",
              borderBottom: "1px solid #e5e7eb",
              padding: "16px 18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "14px",
              borderTopLeftRadius: "24px",
              borderTopRightRadius: "24px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "18px",
                  fontWeight: 950,
                  color: "#0f172a",
                }}
              >
                POS Thermal Receipt Preview
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  fontWeight: 700,
                  marginTop: "2px",
                }}
              >
                Optimized for 80mm thermal printer layout.
              </div>
            </div>

            <div
              className="pos-thermal-actions"
              style={{ display: "flex", gap: "10px", alignItems: "center" }}
            >
              <button
                type="button"
                onClick={handlePrint}
                style={{
                  background: "#111827",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "999px",
                  padding: "10px 18px",
                  fontSize: "13px",
                  fontWeight: 950,
                  cursor: "pointer",
                }}
              >
                Print Receipt
              </button>

              <button
                type="button"
                onClick={onClose}
                style={{
                  background: "#ffffff",
                  color: "#111827",
                  border: "1px solid #d1d5db",
                  borderRadius: "999px",
                  width: "38px",
                  height: "38px",
                  fontSize: "19px",
                  fontWeight: 950,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 320px",
              gap: "24px",
              padding: "24px",
              alignItems: "start",
            }}
          >
            <div className="pos-thermal-no-print">
              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "22px",
                  padding: "20px",
                  marginBottom: "14px",
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    background: "#ecfeff",
                    color: "#0e7490",
                    border: "1px solid #a5f3fc",
                    borderRadius: "999px",
                    padding: "7px 12px",
                    fontSize: "12px",
                    fontWeight: 950,
                    marginBottom: "14px",
                  }}
                >
                  Money Received
                </div>

                <h3
                  style={{
                    margin: 0,
                    fontSize: "28px",
                    fontWeight: 950,
                    color: "#0f172a",
                    letterSpacing: "-0.04em",
                  }}
                >
                  {safeCurrency(payableAmount)}
                </h3>

                <p
                  style={{
                    margin: "8px 0 0",
                    color: "#64748b",
                    fontSize: "13px",
                    lineHeight: 1.6,
                    fontWeight: 700,
                  }}
                >
                  This receipt is designed for POS counter sales where payment is collected immediately.
                </p>
              </div>

              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "22px",
                  padding: "18px",
                }}
              >
                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: 950,
                    color: "#0f172a",
                    marginBottom: "12px",
                  }}
                >
                  Receipt Summary
                </div>

                <div style={{ display: "grid", gap: "10px" }}>
                  <div style={lineStyle}>
                    <strong>Invoice No</strong>
                    <span>{safeValue(salesInvoice?.invoice_no)}</span>
                  </div>
                  <div style={lineStyle}>
                    <strong>Date</strong>
                    <span>
                      {salesInvoice?.date ? formatDate(salesInvoice.date) : "N/A"}
                    </span>
                  </div>
                  <div style={lineStyle}>
                    <strong>Payment Method</strong>
                    <span style={{ textTransform: "capitalize" }}>
                      {safeValue(salesInvoice?.pay_method, "N/A")}
                    </span>
                  </div>
                  <div style={lineStyle}>
                    <strong>Total Items</strong>
                    <span>{products.length}</span>
                  </div>
                  <div style={lineStyle}>
                    <strong>Total Qty</strong>
                    <span>{totalQty}</span>
                  </div>
                </div>
              </div>
            </div>

            <div id="pos-thermal-print-area" style={{ width: "320px", maxWidth: "100%", margin: "0 auto", background: "#ffffff" }}>
              <PrintableInvoice layout={layout} invoice={normalizeInvoiceForPrint(data, "pos")} storeInfo={storeInfo || {}} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default POSThermalInvoiceModal;
